import React, { useEffect, useMemo, useState, useCallback } from "react";
import { observer } from "mobx-react";
import styled from "styled-components";
import { Save, CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { Button } from "@ann/web-components";
import TemplateEditor from "../../../../../elements/v2/TemplateEditor/TemplateEditor";
import TemplateEditorStore from "../../../../../store/components/templateEditor/TemplateEditorStore";
import { useBlastCampaign } from "../../context";
import { useEmailTemplate } from "../../hooks";
import { BINDING_ORIGINS } from "../../constants/blastConstants";

// Simple email validation
const isValidEmail = (email) => {
  if (!email || typeof email !== "string") return false;
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email.trim());
};

// Parse and validate comma-separated emails
const parseEmailList = (value) => {
  if (!value || typeof value !== "string") return [];
  return value.split(",").map((e) => e.trim()).filter(Boolean);
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 600px;
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 8px;
  background: ${(props) => props.theme.colors?.primaryLight || "#EFF6FF"};
  color: ${(props) => props.theme.colors?.primary || "#3B82F6"};
  font-size: 14px;
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 8px;
  background: ${(props) => props.theme.colors?.warningLight || "#FEF3C7"};
  color: ${(props) => props.theme.colors?.warning || "#D97706"};
  font-size: 14px;
`;

const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 8px;
  background: ${(props) => props.theme.colors?.successLight || "#D1FAE5"};
  color: ${(props) => props.theme.colors?.success || "#059669"};
  font-size: 14px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 8px;
  background: ${(props) => props.theme.colors?.errorLight || "#FEE2E2"};
  color: ${(props) => props.theme.colors?.error || "#DC2626"};
  font-size: 14px;

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  ul {
    margin: 4px 0 0 0;
    padding-left: 16px;
  }
`;

const EditorWrapper = styled.div`
  flex: 1;
  border: 1px solid ${(props) => props.theme.colors?.border || "#E5E7EB"};
  border-radius: 8px;
  overflow: hidden;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${(props) => props.theme.colors?.border || "#E5E7EB"};
`;

const EmailTemplateTab = observer(() => {
  const { blastContacts, setEmailPayload, setEmailBindings } = useBlastCampaign();
  const { availableFields, eventSourceList } = useEmailTemplate();
  const [isSaved, setIsSaved] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Create a local TemplateEditorStore instance
  const templateEditorStore = useMemo(() => {
    const store = new TemplateEditorStore();
    return store;
  }, []);

  // Initialize template editor with available variables
  useEffect(() => {
    if (!templateEditorStore) return;

    // Clear and set up variables
    templateEditorStore.clear();

    // Add Email variable (fixed)
    templateEditorStore.variablesEditorStore.addVariable({
      name: "Email",
      variable: "Email",
      origin: BINDING_ORIGINS.FIXED,
      value: "hello@example.com",
    });

    // Add To variable (from contacts)
    templateEditorStore.variablesEditorStore.addVariable({
      name: "To",
      variable: "To",
      origin: BINDING_ORIGINS.EVENT,
      value: "email",
      source: "contacts",
      field: "email",
    });

    // Add variables for each available field
    availableFields.forEach((field) => {
      if (field === "email") return; // Skip, already added as To

      templateEditorStore.variablesEditorStore.addVariable({
        name: field,
        variable: field,
        origin: BINDING_ORIGINS.EVENT,
        value: field,
        source: "contacts",
        field: field,
      });
    });

    // Set default to field (from contacts)
    // Note: "from" should be set by user as a real email, not a template variable
    templateEditorStore.updateField("to", "{{To}}");

    // Set sources for the editor
    templateEditorStore.updateField("sources", eventSourceList);
  }, [templateEditorStore, availableFields, eventSourceList]);

  // Save template to context
  const handleSaveTemplate = useCallback(() => {
    const exportData = templateEditorStore.exportData();
    
    if (!exportData) return;

    const { html, variables, form } = exportData;
    const errors = [];

    // Validate required fields
    if (!form.from || !form.from.trim()) {
      errors.push("From email is required");
    } else if (!isValidEmail(form.from)) {
      errors.push("From email is not a valid email address");
    }

    if (!form.subject || !form.subject.trim()) {
      errors.push("Subject is required");
    }

    if (!html || !html.trim()) {
      errors.push("Email body is required");
    }

    // Validate optional CC emails
    const ccEmails = parseEmailList(form.cc);
    const invalidCc = ccEmails.filter((e) => !isValidEmail(e));
    if (invalidCc.length > 0) {
      errors.push(`Invalid CC email(s): ${invalidCc.join(", ")}`);
    }

    // Validate optional BCC emails
    const bccEmails = parseEmailList(form.bcc);
    const invalidBcc = bccEmails.filter((e) => !isValidEmail(e));
    if (invalidBcc.length > 0) {
      errors.push(`Invalid BCC email(s): ${invalidBcc.join(", ")}`);
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      setIsSaved(false);
      return;
    }

    // Build payload with validated data
    const payload = [
      { field: "from", value: form.from.trim() },
      { field: "sender_name", value: (form.senderName || "").trim() },
      { field: "to", value: form.to || "{{To}}" },
      { field: "subject", value: form.subject.trim() },
      { field: "cc", value: ccEmails.join(",") },
      { field: "bcc", value: bccEmails.join(",") },
      { field: "body", value: html },
    ];

    // Build bindings
    const bindings = (variables || [])
      .filter((v) => v.name)
      .map((variable) => ({
        name: variable.name,
        variable: `{{${variable.variable}}}`,
        origin: variable.origin,
        value: variable.origin === BINDING_ORIGINS.EVENT 
          ? variable.field || variable.value 
          : variable.value,
      }));

    // Ensure To binding exists
    if (!bindings.find((b) => b.name === "To")) {
      bindings.unshift({
        name: "To",
        variable: "{{To}}",
        origin: BINDING_ORIGINS.EVENT,
        value: "email",
      });
    }

    setValidationErrors([]);
    setEmailPayload(payload);
    setEmailBindings(bindings);
    setIsSaved(true);
  }, [templateEditorStore, setEmailPayload, setEmailBindings]);

  // Reset saved state when user might have made changes
  useEffect(() => {
    if (isSaved) {
      // Reset after 3 seconds
      const timer = setTimeout(() => setIsSaved(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isSaved]);

  const hasContacts = blastContacts.length > 0;

  return (
    <Container>
      {!hasContacts && (
        <WarningBanner>
          Please add contacts before configuring your email template. 
          Available template variables depend on your contact data.
        </WarningBanner>
      )}

      {hasContacts && availableFields.length > 0 && !isSaved && (
        <InfoBanner>
          Available variables: {availableFields.join(", ")}. 
          Use {"{{variable_name}}"} syntax in your template.
        </InfoBanner>
      )}

      {validationErrors.length > 0 && (
        <ErrorBanner>
          <ErrorIcon />
          <div>
            <strong>Please fix the following errors:</strong>
            <ul>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        </ErrorBanner>
      )}

      {isSaved && (
        <SuccessBanner>
          <CheckCircle />
          Template saved! You can now send your blast campaign.
        </SuccessBanner>
      )}

      <EditorWrapper>
        <TemplateEditor
          store={templateEditorStore}
          isPopup={false}
          onExportHtml={handleSaveTemplate}
        />
      </EditorWrapper>

      <ActionBar>
        <Button
          type="primary"
          size="medium"
          onClick={handleSaveTemplate}
          disabled={!hasContacts}
          label="Save Template"
        ></Button>
      </ActionBar>
    </Container>
  );
});

export default EmailTemplateTab;
